import React, { Fragment, memo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify';
import styles from './SignUpProvider.module.scss'
import CommonFormFieldWrapper, { getFormInputErrors } from '../../../common/components/form-field-wrapper/CommonFormFieldWrapper'
import HelpForm from '../../login/components/HelpForm/HelpForm'
import { useGenerateEmailVerificationCodeMutation } from '../../../service/user-service'

type Props = {}

type SignUpFormProps = {
    email: string
}

const SignUpProvider: React.FC<Props> = memo((props: Props) => {
    const [ signedUp, setSignedUp ] = useState(false);
    const [showHelpForm, setShowHelpForm ] = useState(false)
    const [generateVerificationCode, {isLoading }] = useGenerateEmailVerificationCodeMutation();

    const navigate = useNavigate();

    const {
        register,
        getValues,
        formState: { errors, isValid },
    } = useForm<SignUpFormProps>({
        defaultValues: {
            email: '',
        },
        reValidateMode: 'onSubmit',
        mode: 'onTouched'
    })

    async function onSubmit() {
        if (!isValid) return;

        try {
            const values = getValues();
            await generateVerificationCode({ ...values, email: values.email.trim() }).unwrap();
            setSignedUp(value => !value);
        } catch (error: any) {
            console.log(error);
            toast.error(error.data?.title || 'Failed to generate verification code');
        }
    }

    const toggleHelpForm = () =>{
		setShowHelpForm(!showHelpForm);
	}

    const gotIt = () => navigate('/')

    return (
        <div className={styles.container}>
            {showHelpForm  && <HelpForm onClose={toggleHelpForm} />}
            {
                !signedUp ?
                <Fragment>
                    <div className={styles.header}>
                        Sign up as a solar provider
                    </div>
                    <div className={styles.contentContainer}>
                        <CommonFormFieldWrapper title='Email address' required errors={getFormInputErrors(errors.email?.message)}>
                            <input
                            type='email'
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                onSubmit();
                                }
                            }} 
                            {...register('email', {
                                required: {
                                    value: true,
                                    message: 'Email is required'
                                },
                                pattern: {
                                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                    message: 'Invalid email'
                                }
                            })} />
                        </CommonFormFieldWrapper>
                        <button disabled={isLoading} className={`standardButton ${styles.button} ${isValid ? '' : styles.disabledButton}`} onClick={onSubmit}> 
                            {isLoading ? 'Submitting...' : 'Proceed'}
                            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.94665 6.00047L0.873576 1.92737C0.735109 1.78892 0.664276 1.61489 0.661076 1.40527C0.657859 1.19568 0.728692 1.01844 0.873576 0.873575C1.01844 0.728692 1.19408 0.65625 1.40048 0.65625C1.60688 0.65625 1.78251 0.728692 1.92738 0.873575L6.4216 5.3678C6.5152 5.46138 6.58123 5.5601 6.61968 5.66395C6.65814 5.76778 6.67738 5.87996 6.67738 6.00047C6.67738 6.12099 6.65814 6.23317 6.61968 6.337C6.58123 6.44085 6.5152 6.53957 6.4216 6.63315L1.92738 11.1274C1.78893 11.2658 1.61489 11.3367 1.40527 11.3399C1.19568 11.3431 1.01844 11.2723 0.873576 11.1274C0.728692 10.9825 0.65625 10.8069 0.65625 10.6005C0.65625 10.3941 0.728692 10.2184 0.873576 10.0736L4.94665 6.00047Z" fill="#F68B1E" />
                        </svg></button>
                        <button onClick={toggleHelpForm} className={`link ${styles.linkButton}`}>
                            Need help?
                        </button>
                    </div>
                </Fragment>
                :
                <div className={styles.notificationContainer}>
                    <div className={styles.notificationImageContainer}>
                        <svg viewBox="0 0 20 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.5199 29.1663V24.4996L0.833984 18.5317V10.3309C0.833984 9.61372 1.08947 8.99925 1.60044 8.48747C2.11139 7.97569 2.72485 7.7198 3.44082 7.7198H5.95365L4.90669 8.76676V0.833008H7.00065V7.7198H13.0006V0.833008H15.0945V8.76676L14.0476 7.7198H16.5604C17.2764 7.7198 17.8898 7.97569 18.4008 8.48747C18.9117 8.99925 19.1672 9.61372 19.1672 10.3309V18.5317L13.4813 24.4996V29.1663H6.5199Z" fill="url(#paint0_linear_1076_6424)" />
                            <defs>
                                <linearGradient id="paint0_linear_1076_6424" x1="7.5" y1="15" x2="14" y2="23" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#F68B1E" />
                                    <stop offset="1" stopColor="#FF5C00" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    <div className={styles.notificationTitle}>
                        Verify your email address
                    </div>
                    <div className={styles.notificationSubtitle}>
                        A mail containing a link to verify your email address has been sent to your inbox.
                    </div>
                    <button className={`standardButton ${styles.notificationButton}`} onClick={gotIt}>
                        Got it!
                    </button>

                </div>
            }
            
            
        </div>
    )
})

export default SignUpProvider